import { AHIcon, AHSeparator, Box } from "arkh";
import React, { useImperativeHandle, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { SetStatusFN } from "./statusCoordinator";
import { useSignalEditSetTitle } from "./useWorkspaceSignalEditSetTitle";
import { useSignalEditSetBody } from "./useWorkspaceSignalEditSetBody";
import { WorkspaceSignalEditMainContent_Signal$key } from "./__generated__/WorkspaceSignalEditMainContent_Signal.graphql";
import { ContentTitleInput } from "../../../../../components/ContentLitstItem";
import { useWorkspaceSignalEditAddFiles } from "./useWorkspaceSignalEditAddFile";

import { WebModal } from "../../../../../TopLevelNavigators/UserLoggedIn/WebModal";
import { Modal } from "react-native";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { WorkspaceFilesScreenComponent } from "../../../WorkspaceFiles/WorkspaceFiles";
import { PendingFile } from "../../../../Files/FileUploadArea";
import cuid from "cuid";

import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { WorkspaceSignalLinkedContent } from "./WorkspaceSignalLinkedContent";
import { sessionId } from "../../../../../OS/sessionId";
import { WebModalHeader } from "../../../WorkspaceTodos/WorkspaceTodoEdit/WorkspaceTodoEditMain/WebModalHeader";
import { RichTextEditor } from "@/src/components/RichText";

export type SignalMainContentRef = {
  showFilesModal: (files: PendingFile[]) => void;
};
export const SignalMainContent = (props: {
  workspaceId: string;
  signal: WorkspaceSignalEditMainContent_Signal$key;
  setStatus: SetStatusFN;
  addImage: (
    uri: string,
    { width, height }: { width: number; height: number }
  ) => void;
  disabled: boolean;
  ref?: React.RefObject<SignalMainContentRef | null>;
}) => {
  const signal = useFragment(
    graphql`
      fragment WorkspaceSignalEditMainContent_Signal on SignalWorkspace {
        id
        title
        body
        formattedBody
        updatedBySessionId

        ...WorkspaceSignalLinkedContent_signal
      }
    `,
    props.signal
  );

  const [title, setTitle] = useSignalEditSetTitle(
    signal.id,
    signal.title,
    props.setStatus
  );

  const [, setBody] = useSignalEditSetBody(
    signal.id,
    {
      body: signal.body ?? "",
      formattedBody: signal.formattedBody ?? "",
    },
    props.setStatus
  );

  const [showModal, setShowModal] = useState(false);
  const [modalKey, setModalKey] = useState(cuid());

  const openModal = () => {
    setShowModal(true);
    setModalKey(cuid());
  };

  const [initialFiles, setInitialFiles] = useState<PendingFile[]>([]);
  useImperativeHandle(props.ref, () => ({
    showFilesModal(files) {
      setInitialFiles(files);
      openModal();
    },
  }));
  const addFiles = useWorkspaceSignalEditAddFiles();

  const intl_tokens = useIntlTokens();

  return (
    <>
      <Box
        style={{ flex: 1, overflow: "hidden", borderWidth: 1 }}
        borderColor="border-primary"
        color={props.disabled ? "transparent" : "surface-primary"}
        cornerRadius="large"
      >
        <ContentTitleInput
          setTitle={setTitle}
          title={title}
          autoFocus
          disabled={props.disabled}
        />
        <AHSeparator />

        <RichTextEditor
          placeholder={intl_tokens.Description}
          initialContent={signal.formattedBody}
          onChange={setBody}
          externalSession={
            signal.updatedBySessionId
              ? signal.updatedBySessionId !== sessionId
              : false
          }
          disabled={props.disabled}
        />

        <WorkspaceSignalLinkedContent
          signal={signal}
          addImage={(uri, { width, height }) => {
            props.addImage(uri, { width, height });
          }}
          openLinkFile={() => {
            setShowModal(true);
          }}
          disabled={props.disabled}
        />
      </Box>
      <Modal
        visible={showModal}
        transparent
        presentationStyle="overFullScreen"
        animationType="fade"
        style={{ flex: 1 }}
      >
        <WebModal>
          <Box style={{ flex: 1 }} color="surface-background-light">
            <WebModalHeader
              close={
                <WebModalHeader.CloseButton
                  onPress={() => setShowModal(false)}
                />
              }
            />
            <LazyQueryNetworkBoundrary>
              {(fetchKey) => (
                <WorkspaceFilesScreenComponent
                  fetchKey={fetchKey}
                  key={modalKey}
                  initialFiles={initialFiles}
                  onFileItemPress={(file) => {
                    addFiles(signal.id, [file.id]);
                    setShowModal(false);
                  }}
                  itemActionComponent={<AHIcon type="add" color="primary" />}
                  onUploadedFiles={(fileIds) => {
                    addFiles(signal.id, fileIds);
                    setShowModal(false);
                  }}
                  workspaceId={props.workspaceId}
                  write
                />
              )}
            </LazyQueryNetworkBoundrary>
          </Box>
        </WebModal>
      </Modal>
    </>
  );
};
